
import { styled, StyleObject } from "styletron-react";
import { colors, getGraphColor } from "./colors";
import { decors } from "./images";
import { mediaQueries } from "./mediaQueries";
import { HTMLAttributes } from "react";
import { FeedCurveComponent } from "../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall";
import { css } from ".";
import { FeedComponentInfo } from "../Services";

// To detect touch devices
// "@media (hover: none) and (pointer: coarse)": {};

type Props = HTMLAttributes<HTMLDivElement>;

interface MainProps {
  $hasScroll?: boolean;
  $focusView?: boolean;
  $zIndex?: number;
  $color?: colors;
  $isHovered?: boolean;
}

// #region | General Components
// Animal Pen Component
export const PenItem = styled<"div", MainProps>("div", (props) => ({
  width: "695px",
  display: "grid",
  gridTemplateColumns: "55px 109px 521px",
  gap: "5px",
  padding: "0px 20px",
  backgroundColor: colors.greyLightSecondary,
  transition: "background-color 0.3s ease",
  ":hover": {
    backgroundColor: props.$isHovered ? colors.greyLightPrimary : undefined,
    cursor: props.$isHovered ? "pointer" : undefined
  },
}));

// Fake Button with Hover
// Can be used for text underline for texts with whitespace wrap
export const HoverButton = styled<"div", MainProps>("div", (props) => ({
  filter: "opacity(1)",
  transition: "all 0.2s ease",
  ":hover": {
    filter: "opacity(0.5)",
    cursor: "pointer",
  }
}));

// Active Indicator
export const Active = styled<"div", MainProps>("div", (props) => ({
  fontSize: "18px",
  color: props.$color ? props.$color : colors.blueLightPrimary,
  animationName: "opacity",
  animationDuration: "2s",
  animationTimingFunction: "ease-in-out",
  animationIterationCount: "infinite",
  animationPlayState: "running",
  "@Keyframes opacity": {
    "0%": { opacity: "0.2" },
    "25%": { opacity: "1" },
    "50%": { opacity: "1" },
    "75%": { opacity: "1" },
    "100%": { opacity: "0.2" }
  }
}));

// Label Component
interface LabelProps {
  $component?: FeedCurveComponent | FeedComponentInfo | number;
  $isHovered?: boolean;
  $width?: string;
}

// If ellipsis on text overflow, wrap text in <p> with style={{whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden"}}:
// Ex:
{/* <frames.GridLabel $component={component?.id} $width={_componentWidth}>{
  <p style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
    {component?.name}
  </p>
}</frames.GridLabel>  */}

function isFeedCurveComponentInfo( comp: FeedCurveComponent | FeedComponentInfo | number | undefined ): comp is FeedComponentInfo {
  return typeof comp === "object" && comp !== null && "id" in comp;
}

function isFeedCurveComponent( comp: FeedCurveComponent | FeedComponentInfo | number | undefined ): comp is FeedCurveComponent {
  return typeof comp === "object" && comp !== null && "componentId" in comp;
}

export const TextLabel = styled<"div", LabelProps>("div", (props) => {
  const componentId = props.$component ? isFeedCurveComponentInfo(props.$component) ? props.$component.id : isFeedCurveComponent(props.$component) ? props.$component.componentId : props.$component : undefined;
  const { graphColor, textColor } = getGraphColor(componentId ?? -1);

  return {
    ...(css.componentLabel as StyleObject),
    color: props.$isHovered ? colors.greyTertiary : colors.greyDarkPrimary,
    padding: undefined,
    height: "15px",
    ":hover": props.$isHovered === undefined ? {} : {
      color: colors.greyTertiary,
    },
  };
});

export const ComponentLabel = styled<"div", LabelProps>("div", (props) => {
  const componentId = props.$component ? isFeedCurveComponentInfo(props.$component) ? props.$component.id : isFeedCurveComponent(props.$component) ? props.$component.componentId : props.$component : undefined;
  const { graphColor, textColor } = getGraphColor(componentId ?? -1);
  
  return {
    ...(css.componentLabel as StyleObject),
    backgroundColor: graphColor,
    color: textColor,
    ":hover": props.$isHovered === undefined ? {} : {
      backgroundColor: graphColor,
      opacity: "0.3",
      cursor: "pointer"
    }
  };
});

export const GridLabel = styled<"div", LabelProps>("div", (props) => {
  const componentId = props.$component ? isFeedCurveComponentInfo(props.$component) ? props.$component.id : isFeedCurveComponent(props.$component) ? props.$component.componentId : props.$component : undefined;
  const { graphColor, textColor } = getGraphColor(componentId ?? -1);

  return {
    ...(css.componentLabel as StyleObject),
    backgroundColor: graphColor,
    color: textColor,
    maxWidth: props.$width ? props.$width : "100px",
    minWidth: "0",
    opacity: props.$isHovered ? "0.3" : "1",
    ":hover": props.$isHovered === undefined ? {} : {
      backgroundColor: graphColor,
      opacity: "0.3",
    }
  };
});

// ---------------------------------------------------
// #endregion

// #region | Def/Login flow Layout
export const Def = styled("div", {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    zIndex: "1",
    position: "relative",
    height: "90vh"
});

export const DecorBg = styled("div", {
    backgroundImage: `url(${decors.def_bg})`,
    backgroundPosition: "center",
    backgroundSize: "cover",
    width: "100vw",
    height: "100vh"
});
// ---------------------------------------------------
// #endregion

// #region | FullViewPanel Grid
// FullViewPanel Layout
const _gridTemplateAreas_fullViewPanel = `
  "logoDisplay page_outer page_outer"
  "globalMenu page_outer page_outer"
  "systemMenu page_outer page_outer"
  "statusBar statusBar languageSelector"
`;

const _gridTemplateAreas_fullViewPanel_mobile = `
  "page_outer"`;

export const FullViewPanel = styled("div", { 
  position: "relative",
  inset: 0,
  display: "Grid",
  gridTemplateColumns: "300px 1fr 280px",
  gridTemplateRows: "150px 350px 1fr 45px",
  gap: "5px",
  gridTemplateAreas: _gridTemplateAreas_fullViewPanel,
  padding: "20px",
  height: "100svh",
  width: "100svw",
  boxSizing: "border-box",
  [mediaQueries.screenHeightMax]: {
      gridTemplateRows: "150px 190px 1fr 45px",
  },
  [mediaQueries.mobile]: {
    gridTemplateAreas: _gridTemplateAreas_fullViewPanel_mobile,
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    zIndex: "999",
    [mediaQueries.mobile]: {
      "@media (hover: none) and (pointer: coarse)": {
        padding: "20px 20px 0px 20px"
      },
    },
    [mediaQueries.horizontalMobile]: {
      "@media (hover: none) and (pointer: coarse)": {
        padding: "20px"
      },
    },
  },
})

export const focus_viewPanel = styled("div", { 
  padding: "20px",
  height: "100dvh",
  boxSizing: "border-box",
  overflow: "hidden"
})

export const LogoDisplay = styled<"div", MainProps>("div", (props) => ({
  backgroundColor: colors.greyDarkPrimary,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  width: props.$focusView ? "100%" : "300px",
  maxWidth: props.$focusView ? "555px" : undefined,
  height: props.$focusView ? "100%" : undefined,
  padding: props.$focusView ? "0px 25px" : undefined,
  boxSizing: props.$focusView ? "border-box" : undefined,
  [mediaQueries.mobile]: {
    width: "100%",
    maxWidth: "555px",
    height: "100%",
    margin: "0px auto",
    padding: "0px 25px",
    boxSizing: "border-box",
  }
}))

export const LanguageSelector = styled("div", {
  gridArea: "languageSelector",
  backgroundColor: colors.greyDarkPrimary,
  borderRadius: "0px 10px 10px 0px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
  [mediaQueries.tablet]: {
    gridArea: "none",
    position: "absolute",
    bottom: "20px",
    right: "20px",
    width: "280px",
    zIndex: "999"
  }
});
  
export const Menu_Outer = styled("div", {
  boxSizing: "border-box",
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "150px 1fr auto",
  rowGap: "10px",
  position: "relative",
  zIndex: "999999",
  padding: "10px",
  height: "100%",
  width: "100%"
})

interface Menu_InnerProps {
  $gridArea?: string
}

export const Menu_Inner = styled<"div", Menu_InnerProps>("div", (props) => ({
  gridArea: props.$gridArea,
  display: "flex",
  alignItems: "end",
  overflow: "hidden",
  maxHeight: "100%",
}))

export const GlobalMenu = styled("div", {
  backgroundColor: colors.greyDarkPrimary,
  borderWidth: "7.5px 1px",
  borderColor: colors.greyDarkPrimary,
  borderStyle: "solid",
  display: "flex",
  justifyContent: "center",
  boxSizing: "border-box",
  [mediaQueries.screenHeightMax]: {
    overflowY: "scroll",
    scrollbarColor: colors.greyDarkSecondary+colors.transparent,
    scrollbarWidth: "thin",
  },
  [mediaQueries.mobile]: {
    position: "relative",
    width: "100%",
    height: "fit-content",
    borderRadius: "10px 10px 0px 0px",
    margin: "0px auto",
    maxWidth: "555px"
  }
});

export const SystemMenu = styled("div", {
  backgroundColor: colors.greyDarkPrimary,
  borderWidth: "7.5px 1px",
  borderColor: colors.greyDarkPrimary,
  borderStyle: "solid",
  flexDirection: "column",
  justifyContent: "center",
  boxSizing: "border-box",
  overflow: "hidden",
  display: "grid",
  gridTemplateRows: "1fr 2px 20px",
  gap: "5px",
  width: "100%",
  gridTemplateColumns: "1fr",
  [mediaQueries.mobile]: {
    width: "100%",
    borderRadius: "10px 10px 0px 0px",
    margin: "0px auto",
    maxWidth: "555px",
    height: "100%"
  }
});

export const SystemList = styled<"div", MainProps>("div", (props) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: props.$focusView ? "end" : "start",
  gap: "5px",
  width: "100%",
  height: "100%",
  overflowY: "scroll",
  scrollbarColor: colors.greyDarkSecondary + colors.greyDarkPrimary,
  scrollbarWidth: "thin",
  padding: "0px 14px",
  boxSizing: "border-box"
}));

export const StatusBar = styled("div", {
  backgroundColor: colors.greyDarkPrimary,
  display: "flex",
  justifyContent: "start",
  alignItems: "center",
  flexDirection: "row",
  borderBottomLeftRadius: "10px",
  padding: "0 10px",
  gap: "10px"
});

export const TabMenu = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "start",
  flexDirection: "row",
  // [mediaQueries.mobile]: {
  //   position: "absolute"
  // }
});
// ---------------------------------------------------
// #endregion

// #region | Page Content
// Grid Page Outer containing TabMenu and Inner Page
const _gridTemplateAreas_page_outer = `
    "tabMenu"
    "inner_page"
`;
const _gridTemplateAreas_page_outer_mobile = `
    "inner_page"
`;
export const Page_outer = styled("div", {
    width: "100%",
  display: "Grid",
    gridArea: "page_outer",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "50px 1fr",
    gridTemplateAreas: _gridTemplateAreas_page_outer,
    overflow: "hidden",
    height: "100%",
    [mediaQueries.mobileXs]: {
      gridTemplateAreas: _gridTemplateAreas_page_outer_mobile,
      gridTemplateRows: "1fr"
    }
})
export const _page_outer_tabMenu = styled("div", { 
    gridArea:"tabMenu", 
    overflowX: "scroll", 
    scrollbarWidth: "none", 
    overflowY: "hidden", 
    position: "relative", 
    [mediaQueries.mobileXs]: { 
      position: "absolute", 
      overflow: "visible", 
    }
})

interface _page_outer_inner_page_Props {
  $toolsMenu?: boolean;
}

export const _page_outer_inner_page = styled<"div", _page_outer_inner_page_Props>("div", (props) => ({
  gridArea:"inner_page", 
  overflow: "hidden", 
  padding: props.$toolsMenu ? "15px 0px 0px 15px" : "15px 0px 15px 15px", 
  [mediaQueries.mobile]: { 
    padding: "15px 0px 0px 0px" 
  }, 
  [mediaQueries.mobileXs]: { 
    padding: "0px"
  },
}))

// Grid Page Inner fullView
const _gridTemplateAreas_page_inner = `
    "_page_inner_header"
    "_page_inner_content"
`;
export const Page_inner = styled("div", {
  height: "100%",
  display: "grid",
  gridAutoColumns: "1fr",
  gridAutoRows: "65px 1fr",
  gap: "10px",
  gridTemplateAreas: _gridTemplateAreas_page_inner,
  [mediaQueries.mobile]: {
    gridAutoRows: "60px 1fr",
  }
});
export const _page_inner_header = styled("div", {
  gridArea: "_page_inner_header",
  height: "100%",
}); 


export const _page_inner_content = styled<"div", MainProps>("div", (props) => ({
  gridArea: "_page_inner_content",
  overflow: "hidden",
  height: "100%",
  boxSizing: "border-box",
  [mediaQueries.mobile]: {
    "@media (hover: none) and (pointer: coarse)": {
      borderTop: props.$hasScroll ? "2px solid " + colors.greyDarkPrimary : "none",
      borderBottom: props.$hasScroll ? "2px solid " + colors.greyDarkPrimary : "none",
      padding: props.$hasScroll ? "5px 0px" : "0px"
    }
  }
}));

export const _page_inner_content_scroll = styled<"div", MainProps>("div", (props) => ({
  gridArea: "_page_inner_content",
  overflowY: "auto",
  scrollbarColor: colors.greyDarkPrimary+colors.transparent,
  scrollbarWidth: "thin",
  height: "100%",
  gap: "10px",
  position: "relative",
}));

// Grid Page Inner focus_view
export const Page_inner_focus_view = styled("div", {
  height: "100%",
  display: "grid",
  gridAutoColumns: "1fr",
  gridAutoRows: "30px 1fr",
  gap: "10px",
  gridTemplateAreas: _gridTemplateAreas_page_inner
});
// ---------------------------------------------------
// #endregion

// #region | ToolsPanel Grid
// ToolsPanel Layout
const _gridTemplateAreas_toolsPanel = `
  "logoDisplay page_outer"
  "globalMenu page_outer"
  "systemMenu page_outer"
`;

const _gridTemplateAreas_toolsPanel_tablet = `
  "page_outer"
`;

export const ToolsPanel = styled("div", { 
    display: "Grid",
    gridTemplateColumns: "300px 1fr",
    gridTemplateRows: "150px 180px 1fr",
    gap: "5px",
    gridTemplateAreas: _gridTemplateAreas_toolsPanel,
    padding: "20px",
    height: "100vh",
    boxSizing: "border-box",
    [mediaQueries.tablet]: {
        gridTemplateAreas: _gridTemplateAreas_toolsPanel_tablet,
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
        zIndex: "0"
    }
})
// ---------------------------------------------------
// #endregion

// #region | Area Layout
export const ToggleBox = styled("div", {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  rowGap: "10px",
  flexWrap: "wrap"
}) as React.FC<Props>;
// ---------------------------------------------------
// #endregion

// #region | Report Pages
export const ReportCard = styled<"div", MainProps>("div", (props) => ({
  cursor: "pointer",
  borderRadius: "10px",
  height: "fit-content",
  breakInside: "avoid",
  border: "5px solid "+colors.greyPrimary, 
  minWidth: "320px",
  width: "100%",
  boxSizing: "border-box",
  filter: 'opacity(1)',
  transition: '0.3s',
  ":hover": {
    filter: 'opacity(0.5)',
  },
}))
// ---------------------------------------------------
// #endregion

// #region | Animal Pages
/* eslint-disable */
export enum AnimalPageAreas {
  timeline = "timeline",
  basic_info = "basic_info",
  view = "view"
}
/* eslint-enable */

const _gridTemplateAreas_animalPage = `
  "${AnimalPageAreas.timeline} ${AnimalPageAreas.timeline}"
  "${AnimalPageAreas.basic_info} ${AnimalPageAreas.view}"
`;

const _gridTemplateAreas_animalPage_mobile = `
  "${AnimalPageAreas.basic_info}"
  "${AnimalPageAreas.view}"
`;

const _gridTemplateAreas_animalPage_noTimeline = `
  "${AnimalPageAreas.basic_info} ${AnimalPageAreas.view}"
`;

export const AnimalPage = styled<"div", MainProps>("div", (props) => ({
  gridArea: "_page_inner_content",
  display: "grid",
  gridTemplateAreas: _gridTemplateAreas_animalPage,
  gridTemplateColumns: "290px 1fr",
  gridTemplateRows: "300px 1fr",
  gap: "20px",
  height: "100%",
  overflow: "hidden",
  boxSizing: "border-box",
  "@media (min-width: 750px) and (max-width: 1000px), (min-width: 1100px)": {
    gridTemplateColumns: "360px 1fr",
  },
  [mediaQueries.mobileXs]: {
    gridTemplateAreas: _gridTemplateAreas_animalPage_mobile,
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr 2fr",
  },
  [mediaQueries.screenHeightMax]: {
    "@media (min-width: 690px)": {
      gridTemplateAreas: _gridTemplateAreas_animalPage_noTimeline,
      gridTemplateColumns: "290px 1fr",
      gridTemplateRows: "1fr",
    }
  }
})) 
// ---------------------------------------------------
// #endregion

// #region | System Overview
/* eslint-disable */
export enum SystemOverviewAreas {
  numberDisplays = "numberDisplays",
  systemControls = "systemControls",
  widgets = "widgets",
  attentions = "attentions"
}
/* eslint-enable */

const _gridTemplateAreas_SystemOverview_desktion = `
  "${SystemOverviewAreas.numberDisplays} ${SystemOverviewAreas.widgets} ${SystemOverviewAreas.attentions}"
  "${SystemOverviewAreas.systemControls} ${SystemOverviewAreas.widgets} ${SystemOverviewAreas.attentions}"
`;

const _gridTemplateAreas_SystemOverview_laptop = `
  "${SystemOverviewAreas.numberDisplays} ${SystemOverviewAreas.widgets}"
  "${SystemOverviewAreas.systemControls} ${SystemOverviewAreas.widgets}"
  "${SystemOverviewAreas.attentions} ${SystemOverviewAreas.widgets}"
`;

const _gridTemplateAreas_SystemOverview_tablet = `
  "${SystemOverviewAreas.numberDisplays} ${SystemOverviewAreas.attentions}"
  "${SystemOverviewAreas.systemControls} ${SystemOverviewAreas.attentions}"
  "${SystemOverviewAreas.widgets} ${SystemOverviewAreas.widgets}"
`;

const _gridTemplateAreas_SystemOverview_mobile = `
  "${SystemOverviewAreas.numberDisplays}"
  "${SystemOverviewAreas.systemControls}"
  "${SystemOverviewAreas.attentions}"
  "${SystemOverviewAreas.widgets}"
`;

export const SystemOverview = styled<"div", MainProps>("div", (props) => ({
  display: "grid",
  gridTemplateAreas: _gridTemplateAreas_SystemOverview_desktion,
  gridTemplateColumns: "320px 1fr 380px",
  gridTemplateRows: "150px 1fr",
  gap: "20px",
  height: "100%",
  overflow: "hidden",
  width: "100%",
  boxSizing: "border-box",
  "@media (max-width: 1849px)": {
    gridTemplateColumns: "320px 1fr 210px"
  },
  "@media (max-width: 1290px)": {
    gridTemplateAreas: _gridTemplateAreas_SystemOverview_laptop,
    gridTemplateColumns: "300px 1fr",
    gridTemplateRows: "150px 410px 1fr",
    overflowY: "scroll",
    scrollbarColor: colors.greyDarkPrimary + colors.transparent,
    scrollbarWidth: "thin",
    padding: "0px 5px"
  },
  "@media (max-width: 720px)": {
    gridTemplateAreas: _gridTemplateAreas_SystemOverview_tablet,
    gridTemplateColumns: "320px 1fr",
    gridTemplateRows: "150px 410px 1fr",
  },
  "@media (max-width: 590px)": {
    gridTemplateAreas: _gridTemplateAreas_SystemOverview_mobile,
    gridTemplateColumns: "1fr",
    gridTemplateRows: "150px auto 580px 1fr",
  }
})) 
// ---------------------------------------------------
// #endregion

// #region | Location Feeder Pages
export const PenListsWrapper = styled("div", {
  gap: "30px",
  flexWrap: "wrap",
  position: "relative",
  display: "flex",
  height: "fit-content",
  width: "fit-content",
  boxSizing: "border-box",
});

export const SectionFrame = styled<"div", MainProps>("div", (props) => ({
  // cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  width: "fit-content",
  height: "fit-content",
  position: "relative",
  opacity: "1",
  transition: "opacity 0.3s ease",
  ":hover": {
    opacity: props.$isHovered ? "0.3" : undefined,
    cursor: props.$isHovered ? "pointer" : undefined
  },
}))

interface LfPenProps {
  $selectable?: boolean;
  $isSelected?: boolean;
  $gridArea: string;
  $primaryColor: colors;
  $textColor: colors;
  $width: string;
  $border: string;
}

export const LfPen = styled<"div", LfPenProps>("div", (props) => ({
  width: props.$width,
  height: "30px",
  textAlign: "center",
  alignContent: "center",
  boxSizing: "border-box",
  padding: "0px 10px",
  transition: "filter 0.3s",
  filter: props.$selectable&& !props.$isSelected ? "opacity(0.5)" : "opacity(1)",
  color: props.$textColor,
  zIndex: "99999",
  cursor: "pointer",
  position: "relative",
  gridArea: props.$gridArea,
  backgroundColor: props.$primaryColor,
  pointerEvents: props.$selectable ? "all" : "none",
  borderLeft: props.$border,
}))
// ---------------------------------------------------
// #endregion

export const StationCard = styled<"div", MainProps>("div", (props) => ({
  borderStyle: "solid",
  borderWidth: "5px",
  borderColor: colors.greyLightSecondary,
  transition: "all 0.3s ease",
  borderRadius: "10px",
  width: "655px",
  height: "fit-content",
  display: "grid",
  gridTemplateColumns: "repeat(2, 50%)",
  gridTemplateRows: "1fr",
  overflow: "hidden",
  boxSizing: "border-box",
  ":hover": {
    cursor: "pointer",
    borderColor: colors.greyLightPrimary
  },
  "& .station_firstChild_left": {
    backgroundColor: colors.greyLightSecondary,
    transition: "all 0.3s ease",
    gridArea: "1/1/2/2",
    height: "200px",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr 2px 45px",
    padding: "20px",
    boxSizing: "border-box",
    gap: "20px"
  },
  ":hover .station_firstChild_left": {
    backgroundColor: colors.greyLightPrimary
  },
  "& .stationNumberSvg": {
    transition: "all 0.3s ease",
    fill: colors.greyLightSecondary
  },
  ":hover .stationNumberSvg": {
    fill: colors.greyLightPrimary
  }
}));






















export const Graph_QL_header = styled("div", {
  textAlign: "center",
});
export const Graph_QL_inner_content_scroll = styled("div", {
    gridArea: "_page_inner_content",
    overflowY: "auto",
    scrollbarColor: colors.greyDarkPrimary/*+colors.transparent*/,
    scrollbarWidth: "auto",
    height: "70vh",
    gap: "10px"
});
export const Graph_QL_sections = styled("div", {
margin: "20px",
"tr": {
paddingLeft: "10px",
},
p: {textAlign: "center"},
});
export const GraphQLTables = styled("div", {
  textAlign: "left",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "& table": {
    width: "100%",
    border: "3px solid" ,
    borderColor: colors.greySecondary,
    borderRadius: "5px",
  },
  "& table tr:nth-child(odd)": {
    backgroundColor: "#D9D9D9",
  }
});

export const GraphQLTableLong = styled("div", {
width: "75%",
});

// to be deleted
const _gridTemplateAreas_table_oneRow = `
    "grid_oneRow_top"
    "grid_oneRow_content"
`;
export const Grid_oneRow = styled("div", {
    width: "100%", 
    height: "100%", 
    display: "grid",
    gridAutoColumns: "1fr",
    gridAutoRows: "auto 1fr", // Auto for responsiveness
    gridTemplateAreas: _gridTemplateAreas_table_oneRow,
});
export const _grid_oneRow_top = styled("div", {
    gridArea: "grid_oneRow_top", 
    backgroundColor: colors.greyPrimary, 
    borderRadius: "10px 10px 0px 0px", 
    display: "flex", 
    flexDirection: "row", 
    alignItems: "center", 
    justifyContent: "space-between", 
    gap: "10px", 
    padding: "10px",
    minHeight: "35px"
}) as React.FC<Props>;

export const _grid_topBtns_outer = styled("div", {
  height: "100%",
  width: "100%",
  display: "grid",
  gridAutoRows: "50px calc(100% - 50px)",
  zIndex: "2",
  position: "relative"
})

export const _grid_topBtns_inner = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "end",
  padding: "0px 20px"
})

export const _grid_bluePrintButton_container = styled("div", {
  position: "absolute",
  bottom: "0px",
  left: "4px",
  zIndex: 99,
  width: "fit-content",
  height: "fit-content",
  cursor: "grab",
  transform: "rotate(90deg)",
  transformOrigin: "left bottom",
});

